import React, { useEffect, useMemo, useState } from "react";
import { Container, Text, Sprite, Graphics } from "@pixi/react";
import * as PIXI from "pixi.js";
import { TextStyle, TextMetrics } from "pixi.js";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import anime from "animejs";
import { closeErrorPopup } from "../../../../State/actions";
import { selectErrorPopupActive, selectErrorPopupCode } from "../../../../State/selectors";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { ASSET_NAME } from "../../../../assets";
import { FONT_CONFIG, TEXT_STYLE_CONFIG } from "../../../../textureConfigs";
import { PixiSettings } from "../../../../pixiSettings";

// Development mode - set to true to test popup with long message
const DEV_MODE = false;

// Overlay settings
const OVERLAY_BACKGROUND_ALPHA = 0.6; // More transparent than default 0.925
const OVERLAY_BACKGROUND_COLOR = 0x000000;
const OVERLAY_FADE_DURATION = 300;

// Popup panel settings
const PANEL_BACKGROUND_COLOR = 0x000000;
const PANEL_BACKGROUND_ALPHA = 0.8;
const PANEL_BORDER_RADIUS = 20;

// Error popup configuration
const ERROR_POPUP_CONFIG = {
  // Gap between text and button (will be multiplied by scale)
  TEXT_BUTTON_GAP: 40,
  // Padding around content inside panel (will be multiplied by scale)
  PANEL_PADDING: 40,

  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.DESKTOP]: {
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        messageFontSize: 24,
        buttonFontSize: 24,
        buttonScale: 1.0,
        textButtonGapMultiplier: 1.2,
        panelPaddingMultiplier: 1.0,
        maxPanelWidthPercent: 0.3, // 40% of screen width
      },
    },
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        messageFontSize: 38,
        buttonFontSize: 38,
        buttonScale: 1.5,
        textButtonGapMultiplier: 1.2,
        panelPaddingMultiplier: 1.5,
        maxPanelWidthPercent: 0.85, // 85% of screen width
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        messageFontSize: 38,
        buttonFontSize: 38,
        buttonScale: 1.2,
        textButtonGapMultiplier: 1.2,
        panelPaddingMultiplier: 1.0,
        maxPanelWidthPercent: 0.3, // 60% of screen width
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        messageFontSize: 36,
        buttonFontSize: 36,
        buttonScale: 1.2,
        textButtonGapMultiplier: 1.2,
        panelPaddingMultiplier: 1.0,
        maxPanelWidthPercent: 0.45, // 70% of screen width
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        messageFontSize: 36,
        buttonFontSize: 36,
        buttonScale: 1.0,
        textButtonGapMultiplier: 1.2,
        panelPaddingMultiplier: 1.0,
        maxPanelWidthPercent: 0.35, // 50% of screen width
      },
    },
  },
};

const WarningPopup = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { dimensions, uiSymbolScale, device, orientation } = useDimensions();
  const errorPopupActive = useSelector(selectErrorPopupActive);
  const errorCode = useSelector(selectErrorPopupCode);

  const isOpen = DEV_MODE ? true : errorPopupActive; // Force open in dev mode

  const [overlayAlpha, setOverlayAlpha] = useState(0);

  // Get device config
  const deviceConfig = ERROR_POPUP_CONFIG.DEVICE_CONFIGS[device]?.[orientation] ||
                       ERROR_POPUP_CONFIG.DEVICE_CONFIGS[PixiSettings.DEVICE.DESKTOP][PixiSettings.ORIENTATION.LANDSCAPE];

  // Use error code directly as translation key
  const translationKey = String(errorCode);
  const errorMessage = errorCode ? t(translationKey) : t("default", { code: errorCode || "unknown" });

  const centerX = dimensions.width / 2;
  const centerY = dimensions.height / 2;

  // Get button texture
  const buttonTexture = PIXI.Assets.get(ASSET_NAME.popi_button_bet_background);

  // Apply scaling
  const scale = uiSymbolScale;
  const buttonWidth = buttonTexture.width * scale * deviceConfig.buttonScale;
  const buttonHeight = buttonTexture.height * scale * deviceConfig.buttonScale;

  // Calculate max content width based on screen size and device config
  const maxPanelWidth = dimensions.width * deviceConfig.maxPanelWidthPercent;
  const maxContentWidth = maxPanelWidth - (ERROR_POPUP_CONFIG.PANEL_PADDING * scale * deviceConfig.panelPaddingMultiplier * 2);

  // Text styling - MUST be before early return
  const messageStyle = useMemo(() => new TextStyle({
    fontFamily: FONT_CONFIG.PRIMARY,
    fontSize: Math.round(deviceConfig.messageFontSize * scale),
    fontWeight: "700",
    fill: 0xffffff,
    align: "center",
    wordWrap: true,
    wordWrapWidth: maxContentWidth,
    ...TEXT_STYLE_CONFIG.OUTLINE,
  }), [deviceConfig.messageFontSize, scale, maxContentWidth]);

  // Button text styling - MUST be before early return
  const buttonTextStyle = useMemo(() => new TextStyle({
    fontFamily: FONT_CONFIG.PRIMARY,
    fontSize: Math.round(deviceConfig.buttonFontSize * scale),
    fontWeight: "700",
    fill: 0xffffff,
    align: "center",
    ...TEXT_STYLE_CONFIG.OUTLINE,
  }), [deviceConfig.buttonFontSize, scale]);

  // Calculate actual text height using TextMetrics - MUST be before early return
  const textMetrics = useMemo(() => {
    return TextMetrics.measureText(errorMessage, messageStyle);
  }, [errorMessage, messageStyle]);

  // Calculate dynamic positions
  const textHeight = textMetrics.height;
  const textWidth = textMetrics.width;
  const gap = ERROR_POPUP_CONFIG.TEXT_BUTTON_GAP * scale * deviceConfig.textButtonGapMultiplier;
  const padding = ERROR_POPUP_CONFIG.PANEL_PADDING * scale * deviceConfig.panelPaddingMultiplier;

  // Calculate panel dimensions based on content
  // Use the larger of text width or button width, ensuring minimum width
  // Cap the width at maxPanelWidth to prevent overly wide panels
  const contentWidth = Math.max(textWidth, buttonWidth);
  const panelWidth = Math.min(contentWidth + padding * 2, maxPanelWidth);
  const panelHeight = textHeight + gap + buttonHeight + padding * 2;

  // Center the panel perfectly in the middle of the screen
  const panelX = centerX - panelWidth / 2;
  const panelY = centerY - panelHeight / 2;

  // Position content - push everything down to add more top padding
  const topPaddingMultiplier = 1.5; // More padding at top
  const messageY = panelY + (padding * topPaddingMultiplier) + textHeight / 2;
  const buttonY = panelY + (padding * topPaddingMultiplier) + textHeight + gap + buttonHeight / 2;

  // Handle overlay fade in/out
  useEffect(() => {
    if (isOpen) {
      // Fade in
      anime({
        targets: { alpha: 0 },
        alpha: OVERLAY_BACKGROUND_ALPHA,
        duration: OVERLAY_FADE_DURATION,
        easing: "easeOutQuad",
        update: (anim) => {
          setOverlayAlpha(anim.animations[0].currentValue);
        },
      });
    } else {
      // Fade out
      anime({
        targets: { alpha: OVERLAY_BACKGROUND_ALPHA },
        alpha: 0,
        duration: OVERLAY_FADE_DURATION,
        easing: "easeInQuad",
        update: (anim) => {
          setOverlayAlpha(anim.animations[0].currentValue);
        },
        complete: () => {
          setOverlayAlpha(0);
        },
      });
    }
  }, [isOpen]);

  const handleClose = () => {
    dispatch(closeErrorPopup());
  };

  // Early return AFTER all hooks
  if (!isOpen) {
    return null;
  }

  return (
    <Container name="warning_popup" zIndex={10000} sortableChildren={true}>
      {/* Black overlay - NOT clickable, more transparent */}
      <Graphics
        draw={(g) => {
          g.clear();
          g.beginFill(OVERLAY_BACKGROUND_COLOR);
          g.drawRect(0, 0, dimensions.width, dimensions.height);
          g.endFill();
        }}
        alpha={overlayAlpha}
        eventMode="static"
        pointerdown={(e) => {
          e.stopPropagation();
          // Do nothing - user cannot close by clicking overlay
        }}
        zIndex={10000}
      />

      {/* Content container */}
      <Container name="popup_content" zIndex={10001}>
        {/* Dark panel background - wraps text and button */}
        <Graphics
          draw={(g) => {
            g.clear();
            g.beginFill(PANEL_BACKGROUND_COLOR, PANEL_BACKGROUND_ALPHA);
            g.drawRoundedRect(
              panelX,
              panelY,
              panelWidth,
              panelHeight,
              PANEL_BORDER_RADIUS * scale
            );
            g.endFill();
          }}
        />

        {/* Error message - dynamically positioned based on text height */}
        <Text
          text={errorMessage}
          style={messageStyle}
          anchor={0.5}
          x={centerX}
          y={messageY}
          resolution={2}
        />

        {/* OK Button - positioned below text with dynamic gap */}
        <Container
          name="ok_button"
          x={centerX}
          y={buttonY}
          eventMode="static"
          cursor="pointer"
          pointerdown={(e) => {
            e.stopPropagation();
            handleClose();
          }}
        >
          <Sprite
            texture={buttonTexture}
            anchor={0.5}
            width={buttonWidth}
            height={buttonHeight}
          />
          <Text
            text={t("ok")}
            style={buttonTextStyle}
            anchor={0.5}
            resolution={2}
          />
        </Container>
      </Container>
    </Container>
  );
};

export default WarningPopup;
